import type { SignInType, SignUpType } from '@/model/zod/authZod';
import type React from 'react';
import { useEffect, useState, type InputHTMLAttributes } from 'react';
import type { FieldErrors, FieldError } from 'react-hook-form';

import { Asset, AssetName } from '@/@shared/components/primitives/Asset';
import styles from '@/@shared/components/forms/AuthInput/AuthInput.module.scss';
import { useTransAuth } from '@/@shared/hooks/useTranslations';
import { clc } from '@/@shared/utils/classComposer';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
	errors: FieldErrors<any>;
	keyName: string;
	label?: string;

	children?: React.ReactNode;
	verified?: boolean;
	setValue?: (name: any, value: any) => void;
	getValues?: (payload?: string | string[]) => SignUpType | SignInType | unknown;
	onEnterPress?: () => void;
}

const AuthInput = ({
	errors,
	keyName,
	label,
	children,
	verified,
	setValue,
	getValues,
	className,
	onEnterPress,
	...props
}: Props) => {
	const [capsLock, setCapsLock] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [hasValue, setHasValue] = useState(false);
	const [clearButtonActive, setClearButtonActive] = useState(false);

	const t = useTransAuth('error');

	// 고유 ID 생성 (접근성)
	const inputId = `input-${keyName}`;
	const errorId = `error-${keyName}`;

	const checkCapsLock = (e: React.KeyboardEvent) => {
		setCapsLock(e.getModifierState('CapsLock'));
	};

	const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsFocused(true);
		// 자동완성된 값 체크
		setTimeout(() => {
			setHasValue(e.target.value.length > 0);
		}, 0);
	};
	const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsFocused(false);
		// blur 시에도 자동완성된 값 체크
		setHasValue(e.target.value.length > 0);
	};
	const handleMouseEnter = () => setIsHovered(true);
	const handleMouseLeave = () => setIsHovered(false);
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setHasValue(e.target.value.length > 0);
		props.onChange?.(e);
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		// paste 이벤트 후 값 업데이트를 위해 setTimeout 사용
		setTimeout(() => {
			const target = e.target as HTMLInputElement;
			setHasValue(target.value.length > 0);

			// react-hook-form에 값 변경 알리기 위해 onChange 이벤트 수동 트리거
			if (props.onChange) {
				const syntheticEvent = {
					target,
					currentTarget: target,
				} as React.ChangeEvent<HTMLInputElement>;
				props.onChange(syntheticEvent);
			}
		}, 0);
		props.onPaste?.(e);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		checkCapsLock(e);
		if (e.key === 'Enter' && onEnterPress) {
			e.preventDefault();
			onEnterPress();
		}
		props.onKeyDown?.(e);
	};

	const capsLockEl = (type: string | undefined) => {
		// 공통 접근성 속성
		const accessibilityProps = {
			id: inputId,
			'aria-invalid': hasError,
			'aria-describedby': hasError ? errorId : undefined,
			'aria-required': props.required,
		};

		if (type === 'password') {
			if (keyName.includes('getPasswords')) {
				return (
					<>
						<input
							className={clc(styles.input, className)}
							onKeyDown={handleKeyDown}
							onKeyUp={checkCapsLock}
							onFocus={handleFocus}
							onBlur={handleBlur}
							onMouseEnter={handleMouseEnter}
							onMouseLeave={handleMouseLeave}
							onChange={handleChange}
							onPaste={handlePaste}
							{...accessibilityProps}
							{...props}
						/>
						{capsLock && (
							<div className={styles.errorArea}>
								<span className={styles.error}>{t('confirmPassword.capsLockError')}</span>
							</div>
						)}
					</>
				);
			}
			return (
				<>
					<input
						className={clc(styles.input, className)}
						onKeyDown={handleKeyDown}
						onKeyUp={checkCapsLock}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						onChange={handleChange}
						onPaste={handlePaste}
						{...accessibilityProps}
						{...props}
					/>
					{capsLock && (
						<div className={styles.errorArea}>
							<span className={styles.error}>{t('password.capsLockError')}</span>
						</div>
					)}
				</>
			);
		}

		if (children) {
			return (
				<div className={styles.row}>
					<div style={{ position: 'absolute' }}>{children}</div>
					<input
						className={clc(styles.input, className)}
						onKeyDown={handleKeyDown}
						onKeyUp={checkCapsLock}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						onChange={handleChange}
						onPaste={handlePaste}
						{...accessibilityProps}
						{...props}
					/>
				</div>
			);
		}
		return (
			<input
				className={clc(styles.input, className)}
				onKeyDown={handleKeyDown}
				onKeyUp={checkCapsLock}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onChange={handleChange}
				onPaste={handlePaste}
				{...accessibilityProps}
				{...props}
			/>
		);
	};

	const getErrorMessage = (): string => {
		// Nested 필드 처리 (getPasswords.getPassword, getPasswords.confirmPassword)
		if (keyName.includes('getPasswords')) {
			const [keyName1, keyName2] = keyName.split('.') as ['getPasswords', 'getPassword' | 'confirmPassword'];

			// 방법 1: errors.getPasswords.getPassword (nested object 형식)
			const parentError = errors[keyName1];
			if (parentError && typeof parentError === 'object' && !('message' in parentError)) {
				const nestedErrors = parentError as Record<string, { message?: string }>;
				const nestedMessage = nestedErrors[keyName2]?.message;
				if (nestedMessage && typeof nestedMessage === 'string') {
					return t(nestedMessage);
				}
			}

			// 방법 2: errors['getPasswords.getPassword'] (flat string key 형식)
			// FieldErrors는 인덱스 시그니처를 가지고 있으므로 Record로 캐스팅하여 안전하게 접근
			const flatErrors = errors as Record<string, FieldError | undefined>;
			const flatError = flatErrors[keyName];
			if (flatError && typeof flatError === 'object' && 'message' in flatError) {
				const message = flatError.message;
				if (typeof message === 'string') {
					return t(message);
				}
			}

			return '';
		} else {
			if (typeof keyName !== 'string') return '';
			const error = errors[keyName as string];
			if (error && typeof error === 'object' && 'message' in error) {
				const message = error.message;
				if (typeof message === 'string') {
					return t(message);
				}
			}
			return '';
		}
	};

	const errorMessage = getErrorMessage();
	const hasError = !!errorMessage;

	const iconBasedOnType = (type: string | undefined) => {
		const iconMap: Record<string, { default: AssetName; active: AssetName }> = {
			email: {
				default: 'auth-action-email-default-svg',
				active: 'auth-action-email-active-svg',
			},
			number: {
				default: 'auth-action-code-default-svg',
				active: 'auth-action-code-active-svg',
			},
			password:
				keyName === 'getPasswords.confirmPassword'
					? {
							default: 'auth-action-confirm-password-default-svg',
							active: 'auth-action-confirm-password-active-svg',
						}
					: {
							default: 'auth-action-password-default-svg',
							active: 'auth-action-password-active-svg',
						},
		};

		const icons = iconMap[type as keyof typeof iconMap];

		// 활성 상태 판단: hover, focus, 또는 값이 있으면 active
		const isActive = isHovered || isFocused || hasValue;

		return icons ? (
			<div className={styles.leftIconArea}>
				<Asset type="icon" name={isActive ? icons.active : icons.default} size="md" className={styles.icon} alt="" />
			</div>
		) : null;
	};

	const handleInputErase = () => setValue && setValue(keyName, '');

	const handleEraseKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleInputErase();
		}
	};

	const checkOrEraseBasedOnType = (type: string | undefined) => {
		// hasValue 상태를 직접 사용 (이미 onChange에서 업데이트됨)
		if (!hasValue) return null;

		if (type === 'password' || type === 'email' || type === 'number') {
			const clearIconName: AssetName = clearButtonActive
				? 'auth-action-clear-purple-active-svg'
				: 'auth-action-clear-purple-default-svg';

			return (
				<div
					className={styles.rightIconArea}
					onClick={handleInputErase}
					onKeyDown={handleEraseKeyDown}
					onMouseEnter={() => setClearButtonActive(true)}
					onMouseLeave={() => setClearButtonActive(false)}
					onFocus={() => setClearButtonActive(true)}
					onBlur={() => setClearButtonActive(false)}
					role="button"
					tabIndex={0}
					aria-label="Clear input"
				>
					<Asset type="icon" name={clearIconName} size="md" className={styles.icon} alt="" />
				</div>
			);
		}
		return null;
	};

	return (
		<label className={styles.label} htmlFor={inputId}>
			{iconBasedOnType(props.type)}
			{capsLockEl(props.type)}
			{checkOrEraseBasedOnType(props.type)}

			{!capsLock && errorMessage && (
				<div className={styles.errorArea} id={errorId} role="alert" aria-live="polite">
					<span className={styles.error}>{errorMessage}</span>
				</div>
			)}
		</label>
	);
};

export default AuthInput;
